import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { getCurrentUser, changePassword, updateUserProfile } from '../api/axiosClient';
import { UserMeResponse } from '../api/types';
import { toast } from 'react-hot-toast';
import { translateRole } from '../lib/utils';
import { 
  User, 
  Shield, 
  Eye,
  EyeOff,
  Hash,
  Edit3Icon,
  XIcon,
  Save,
} from 'lucide-react';

const AccountView: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<UserMeResponse['data'] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Password validation
  const validatePassword = (password: string) => {
    const isNumeric = /^\d+$/.test(password);
    const isLength6 = password.length === 6;
    
    return {
      isValid: isNumeric && isLength6,
      isNumeric,
      isLength6
    };
  };

  // Form states
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: ''
  });

  const newPasswordValidation = validatePassword(formData.newPassword);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [profileForm, setProfileForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  // Fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const response = await getCurrentUser();
        if (response.success && response.data) {
          setCurrentUser(response.data);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
        toast.error('Kullanıcı bilgileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

    useEffect(() => {
      if (currentUser) {
        const full = (currentUser.fullName || "").trim();
        const parts = full ? full.split(" ") : [];
        const firstName = parts.length ? parts.slice(0, -1).join(" ") || parts[0] : "";
        const lastName = parts.length > 1 ? parts[parts.length - 1] : "";

        setProfileForm({
          firstName: (currentUser as any).firstName ?? firstName,
          lastName: (currentUser as any).lastName ?? lastName,
          email: currentUser.email || "",
        });
      }
      
    }, [currentUser]);

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSaveProfile = async () => {
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      toast.error("Ad ve soyad zorunludur");
      return;
    }
    if (!isValidEmail(profileForm.email.trim())) {
      toast.error("Geçerli bir e-posta girin");
      return;
    }

    setIsSavingProfile(true);
    try {
      const payload = {
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        email: profileForm.email.trim(),
      };

      const res = await updateUserProfile(payload);

      if (res.success) {
        toast.success("Profil güncellendi");
        setIsEditingProfile(false);


  setCurrentUser((prev) =>
    prev
      ? ({
          ...prev,
          email: payload.email,
          fullName: `${payload.firstName} ${payload.lastName}`.trim(),
          firstName: payload.firstName,
          lastName: payload.lastName,
        } as any)
      : prev,
  );

  const me = await getCurrentUser();
  if (me.success && me.data) {
    setCurrentUser(me.data);
  }


      } else {
        toast.error(res.message || "Profil güncellenirken hata oluştu");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Profil güncellenirken hata oluştu");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancelProfileEdit = () => {
    if (currentUser) {
      const full = (currentUser.fullName || "").trim();
      const parts = full ? full.split(" ") : [];
      const firstName = parts.length ? parts.slice(0, -1).join(" ") || parts[0] : "";
      const lastName = parts.length > 1 ? parts[parts.length - 1] : "";

      setProfileForm({
        firstName: (currentUser as any).firstName ?? firstName,
        lastName: (currentUser as any).lastName ?? lastName,
        email: currentUser.email || "",
      });
    }
    setIsEditingProfile(false);
  };

  const handleChangePassword = async () => {
    if (!newPasswordValidation.isValid) {
      if (!newPasswordValidation.isNumeric) {
        toast.error('Şifre sadece rakamlardan oluşmalıdır');
      } else if (!newPasswordValidation.isLength6) {
        toast.error('Şifre 6 haneli olmalıdır');
      }
      return;
    }

    try {
      const response = await changePassword(formData.currentPassword, formData.newPassword);
      
      if (response.success) {
        toast.success('Şifre başarıyla değiştirildi');
        setFormData({
          currentPassword: '',
          newPassword: ''
        });
      } else {
        toast.error(response.message || 'Şifre değiştirilirken hata oluştu');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      const errorMessage = error.response?.data?.message || 'Şifre değiştirilirken hata oluştu';
      toast.error(errorMessage);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B51E0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="flex items-center space-x-2">
            <User className="w-5 h-5" />
            <span>Profil Bilgileri</span>
          </CardTitle>
          <CardDescription>Kişisel bilgilerinizi görüntüleyin</CardDescription>
        </div>

        {!isEditingProfile ? (
          <Button variant="outline" size="sm" onClick={() => setIsEditingProfile(true)}>
            <Edit3Icon className="w-4 h-4 mr-2" />
            Düzenle
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancelProfileEdit}
              disabled={isSavingProfile}
            >
              <XIcon className="w-4 h-4 mr-2" />
              İptal
            </Button>
            <Button
              size="sm"
              onClick={handleSaveProfile}
              disabled={isSavingProfile}
              className="bg-[#9B51E0] hover:bg-[#8B4AD9] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSavingProfile ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Ad */}
        <div className="space-y-2">
          <Label>Ad</Label>
          {isEditingProfile ? (
            <input
              value={profileForm.firstName}
              onChange={(e) => setProfileForm((p) => ({ ...p, firstName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Ad"
            />
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
              {(currentUser as any)?.firstName || (currentUser?.fullName?.split(" ").slice(0, -1).join(" ") || "N/A")}
            </div>
          )}
        </div>

        {/* Soyad */}
        <div className="space-y-2">
          <Label>Soyad</Label>
          {isEditingProfile ? (
            <input
              value={profileForm.lastName}
              onChange={(e) => setProfileForm((p) => ({ ...p, lastName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Soyad"
            />
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
              {(currentUser as any)?.lastName || (currentUser?.fullName?.split(" ").slice(-1)[0] || "N/A")}
            </div>
          )}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label>E-posta</Label>
          {isEditingProfile ? (
            <input
              type="email"
              value={profileForm.email}
              onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="E-posta"
            />
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
              {currentUser?.email || "N/A"}
            </div>
          )}
        </div>

        {/* UserName sabit */}
        <div className="space-y-2">
          <Label>Kullanıcı Adı</Label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900">
            {currentUser?.userName || "N/A"}
          </div>
        </div>

        {/* Rol sabit */}
        <div className="space-y-2">
          <Label>Rol</Label>
          <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
            <Shield className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-gray-700">
              {translateRole(currentUser?.role)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>


        {/* Password Change */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Hash className="w-5 h-5" />
              <span>Şifre Değiştir</span>
            </CardTitle>
            <CardDescription>
              Hesap güvenliğiniz için şifrenizi güncelleyin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Hash className="w-4 h-4 text-gray-500" />
                <span>Mevcut Şifre <span className="text-red-500">*</span></span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="••••••"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="w-full pr-10 text-left text-sm font-mono px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                <Hash className="w-4 h-4 text-gray-500" />
                <span>Yeni Şifre <span className="text-red-500">*</span></span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="••••••"
                  value={formData.newPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full pr-10 text-left text-sm font-mono px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <div className={`w-1/2 h-2 rounded-full ${
                      newPasswordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                    <div className={`w-1/2 h-2 rounded-full ${
                      newPasswordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-200'
                    }`}></div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div className={`flex items-center space-x-2 ${newPasswordValidation.isNumeric ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${newPasswordValidation.isNumeric ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span>Sadece rakam</span>
                    </div>
                    <div className={`flex items-center space-x-2 ${newPasswordValidation.isLength6 ? 'text-green-600' : 'text-gray-400'}`}>
                      <div className={`w-2 h-2 rounded-full ${newPasswordValidation.isLength6 ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span>6 haneli</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Button 
              onClick={handleChangePassword}
              className="w-full"
              disabled={!formData.currentPassword || !formData.newPassword || !newPasswordValidation.isValid}
            >
              Şifreyi Değiştir
            </Button>
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default AccountView;
